
import { useState, useEffect } from "react";
import { 
  Archive, 
  Check, 
  X, 
  Clock,  
  Filter, 
  RefreshCw, 
  ArrowUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useIsMobile } from "@/hooks/use-mobile";
import { useApprovals, ApprovalPost } from "@/hooks/use-approvals";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";

export default function ArchivedApprovals() {
  const { loadArchivedApprovals, restoreApproval } = useApprovals();
  const [archivedApprovals, setArchivedApprovals] = useState<ApprovalPost[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isRestoring, setIsRestoring] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  
  const loadArchived = async () => {
    setIsLoading(true);
    try {
      const archived = await loadArchivedApprovals();
      setArchivedApprovals(archived);
    } catch (error) {
      console.error("Error loading archived approvals:", error);
      toast.error("Erro ao carregar aprovações arquivadas");
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    loadArchived();
  }, []);
  
  const filteredApprovals = archivedApprovals.filter(item => 
    item.client_name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    item.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleRestore = async (id: string) => {
    setIsRestoring(id);
    try {
      await restoreApproval(id);
      // Remove from archived list
      setArchivedApprovals(prev => prev.filter(item => item.id !== id));
      toast.success("Aprovação restaurada com sucesso!");
    } catch (error) {
      console.error("Error restoring:", error);
      toast.error("Erro ao restaurar aprovação");
    } finally {
      setIsRestoring(null);
    }
  };

  const getStatusBadge = (status: string) => {
    return (
      <Badge variant="outline" className="bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-800/50">
        <Archive className="h-3 w-3 mr-1" /> Arquivado
      </Badge>
    );
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case "instagram":
        return "Instagram";
      case "facebook":
        return "Facebook";
      case "linkedin":
        return "LinkedIn";
      default:
        return "Outro";
    }
  };

  if (isLoading) {
    return (
      <div className="w-full py-4 sm:py-6 flex items-center justify-center">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <div className="w-full py-4 sm:py-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Aprovações Arquivadas</h1>
          <p className="text-muted-foreground">
            Visualize e restaure aprovações que foram arquivadas
          </p>
        </div>
        <div className="flex gap-2 mt-4 sm:mt-0">
          <Button variant="outline" onClick={() => navigate('/approvals')}>
            <ArrowUp className="mr-2 h-4 w-4" /> Voltar para Aprovações
          </Button>
          <Button variant="outline" onClick={loadArchived}>
            <RefreshCw className="mr-2 h-4 w-4" /> Atualizar
          </Button>
        </div>
      </div>
      
      <div className="flex flex-col md:flex-row gap-4 mb-4 sm:mb-6">
        <div className="flex-1">
          <Input
            placeholder="Buscar por cliente ou título..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full"
          />
        </div>
        <div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size={isMobile ? "sm" : "default"}>
                <Filter className="h-4 w-4 mr-2" />
                Filtrar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuGroup>
                <DropdownMenuItem>Todos os clientes</DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {filteredApprovals.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {filteredApprovals.map((item) => (
            <Card key={item.id} className="overflow-hidden">
              <CardHeader className="p-3 sm:p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-base break-words">{item.title}</CardTitle>
                    <CardDescription>Cliente: {item.client_name}</CardDescription>
                  </div>
                  <div className="flex gap-2 items-center">
                    {getStatusBadge(item.status)}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0 space-y-3 sm:space-y-4">
                <div className="aspect-video overflow-hidden rounded-md bg-gray-100 dark:bg-gray-800">
                  <img 
                    src={item.image_url || ''} 
                    alt={item.title} 
                    className="w-full h-full object-cover" 
                  />
                </div>
                
                <div className="flex flex-col space-y-1.5">
                  <div className="text-sm">
                    <span className="font-medium">Plataforma:</span> {getPlatformIcon(item.platform)}
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Data:</span> {format(new Date(item.created_at), "dd 'de' MMM", { locale: ptBR })}
                  </div>
                  {item.updated_at && (
                    <div className="text-sm">
                      <span className="font-medium">Arquivado em:</span> {format(new Date(item.updated_at), "dd 'de' MMM, yyyy", { locale: ptBR })}
                    </div>
                  )}
                </div>
                
                {item.feedback && (
                  <div className="bg-gray-50 dark:bg-gray-900/20 p-3 rounded-md border border-gray-100 dark:border-gray-800/30">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-400">Feedback:</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 break-words">{item.feedback}</p>
                  </div>
                )}
                
                <Button 
                  className="w-full"
                  variant="outline"
                  onClick={() => handleRestore(item.id)}
                  disabled={isRestoring === item.id}
                >
                  {isRestoring === item.id ? (
                    <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2"></div>
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Restaurar
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 sm:py-12 border rounded-lg">
          <Archive className="mx-auto h-12 w-12 text-muted-foreground/70 mb-4" />
          <h3 className="text-lg sm:text-xl font-medium mb-2">Nenhuma aprovação arquivada</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            Quando você arquivar aprovações, elas aparecerão aqui para referência futura.
          </p>
        </div>
      )}
      
      {isMobile && <div className="h-20"></div>}
    </div>
  );
}
